import React, { useCallback, useEffect, useState } from 'react';
import {
  Stack, Typography, Button, Box, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, CircularProgress, Menu, MenuItem, Grid,
} from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { SalonMember, TaskDetail, TaskTimeSession, taskApi, taskErrorMessage } from '../../services/taskService';
import { useTaskTimerStore } from '../../store/taskTimerStore';
import { formatClock, formatDate, formatDateTime, formatDuration, userLabel } from './taskShared';
import { withMemberName } from './TaskHistory';
import { SectionCard, StatusPill, PersonAvatar } from '../../components/ui';
import { tokens } from '../../theme';
import { formatTime } from '../../utils/format';

interface Props {
  task: TaskDetail;
  currentUserId: string;
  onChanged: () => void;
  onNotify: (msg: string, severity: 'success' | 'error' | 'info') => void;
  /** Lista osob salonu — do podpisu sesji imieniem i nazwiskiem. */
  members?: SalonMember[];
}

const pad = (n: number) => String(n).padStart(2, '0');
/** ISO instant → value accepted by <input type="datetime-local"> (local time). */
const toLocalInput = (iso: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const REASON_ERROR = 'Podaj powód korekty (min. 3 znaki).';

/** „12.09.2026 · 09:15–10:40” albo pelne daty, gdy sesja przeszla przez polnoc. */
const sessionRange = (s: TaskTimeSession): string => {
  if (!s.endedAt) return `${formatDateTime(s.startedAt)} – teraz`;
  if (formatDate(s.startedAt) === formatDate(s.endedAt)) {
    return `${formatDate(s.startedAt)} · ${formatTime(s.startedAt)}–${formatTime(s.endedAt)}`;
  }
  return `${formatDateTime(s.startedAt)} – ${formatDateTime(s.endedAt)}`;
};

const Figure: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted, lineHeight: 1.35 }}>{label}</Typography>
    <Typography sx={{ fontSize: '1rem', fontWeight: 700, mt: 0.25, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{value}</Typography>
  </Box>
);

export const TaskTimerPanel: React.FC<Props> = ({ task, currentUserId, onChanged, onNotify, members }) => {
  const { session, conflict, loading, start, pause, stop, refresh, clearConflict, elapsedSeconds } =
    useTaskTimerStore();
  const [, forceTick] = useState(0);
  const [noteDialog, setNoteDialog] = useState<{ note: string } | null>(null);
  const [correcting, setCorrecting] = useState<TaskTimeSession | null>(null);
  const [correctForm, setCorrectForm] = useState({ startedAt: '', endedAt: '', reason: '', note: '' });
  const [correctError, setCorrectError] = useState('');
  const [deleting, setDeleting] = useState<{ session: TaskTimeSession; reason: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [sessionMenu, setSessionMenu] = useState<{ el: HTMLElement; session: TaskTimeSession } | null>(null);

  const runningHere = session?.taskId === task.id;

  // Re-render once a second while a measurement is running on this task.
  useEffect(() => {
    if (!runningHere) return undefined;
    const t = setInterval(() => forceTick((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, [runningHere]);

  // Re-sync with the server periodically so a session closed elsewhere
  // (another device, an admin correction) is reflected here too.
  useEffect(() => {
    const t = setInterval(() => refresh(), 60_000);
    return () => clearInterval(t);
  }, [refresh]);

  const handleStart = useCallback(async (force = false) => {
    setBusy(true);
    const ok = await start(task.id, force);
    setBusy(false);
    if (ok) {
      onChanged();
      onNotify('Pomiar czasu uruchomiony.', 'success');
    }
  }, [start, task.id, onChanged, onNotify]);

  const handlePause = async () => {
    setBusy(true);
    await pause();
    setBusy(false);
    onChanged();
    onNotify('Pomiar wstrzymany. Zapisany czas został doliczony.', 'info');
  };

  const handleStop = async (note?: string) => {
    setBusy(true);
    await stop(note);
    setBusy(false);
    setNoteDialog(null);
    onChanged();
    onNotify('Pomiar zakończony.', 'success');
  };

  const openCorrection = (s: TaskTimeSession) => {
    setCorrecting(s);
    setCorrectError('');
    setCorrectForm({
      startedAt: toLocalInput(s.startedAt),
      endedAt: s.endedAt ? toLocalInput(s.endedAt) : '',
      reason: '',
      note: s.note || '',
    });
  };

  const submitCorrection = async () => {
    if (!correcting) return;
    if (correctForm.reason.trim().length < 3) {
      setCorrectError(REASON_ERROR);
      return;
    }
    try {
      await taskApi.correctSession(correcting.id, {
        startedAt: new Date(correctForm.startedAt).toISOString(),
        endedAt: correctForm.endedAt ? new Date(correctForm.endedAt).toISOString() : undefined,
        note: correctForm.note,
        reason: correctForm.reason.trim(),
      });
      setCorrecting(null);
      onChanged();
      onNotify('Wpis czasu został poprawiony. Zmiana zapisana w historii.', 'success');
    } catch (err) {
      setCorrectError(taskErrorMessage(err, 'Nie udało się zapisać korekty.'));
    }
  };

  const submitDelete = async () => {
    if (!deleting) return;
    try {
      await taskApi.deleteSession(deleting.session.id, deleting.reason.trim());
      setDeleting(null);
      onChanged();
      onNotify('Wpis czasu został usunięty.', 'success');
    } catch (err) {
      onNotify(taskErrorMessage(err, 'Nie udało się usunąć wpisu.'), 'error');
    }
  };

  const canCorrect = (s: TaskTimeSession) =>
    !!s.endedAt && (task.permissions.canManage || s.userId === currentUserId);
  const canDeleteSession = (s: TaskTimeSession) => task.permissions.canManage && !!s.endedAt;

  const closedSessions = task.sessions.filter((s) => s.endedAt);
  const canRun = task.permissions.canRunTimer && task.status !== 'DONE' && task.status !== 'CANCELLED';
  const reasonFieldError = correctError === REASON_ERROR;

  return (
    <>
      <SectionCard title="Pomiar czasu" subtitle="Czas pracy nad tym zadaniem">
        <Box
          sx={{
            borderRadius: '12px',
            bgcolor: runningHere ? tokens.successSoft : tokens.surface,
            px: 2,
            py: 2.5,
            textAlign: 'center',
            transition: 'background-color .2s ease',
          }}
        >
          <StatusPill
            size="small"
            label={runningHere ? 'Trwa pomiar' : 'Pomiar zatrzymany'}
            tone={runningHere ? 'success' : 'neutral'}
          />
          <Typography
            role="timer"
            aria-label="Czas bieżącego pomiaru"
            sx={{
              mt: 1,
              fontSize: { xs: '2.25rem', sm: '2.5rem' },
              fontWeight: 750,
              lineHeight: 1.1,
              letterSpacing: '-0.01em',
              fontVariantNumeric: 'tabular-nums',
              color: runningHere ? tokens.success : tokens.subtle,
            }}
          >
            {formatClock(runningHere ? elapsedSeconds() : 0)}
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: tokens.inkSoft, mt: 0.75 }}>
            {runningHere && session
              ? `Rozpoczęty o ${formatTime(session.startedAt)}`
              : canRun
                ? 'Włącz, gdy zaczynasz pracę nad zadaniem.'
                : task.status === 'DONE' || task.status === 'CANCELLED'
                  ? 'Zadanie jest zamknięte — pomiar czasu jest niedostępny.'
                  : 'Pomiar mogą uruchomić tylko osoby przypisane do zadania.'}
          </Typography>

          <Stack direction="row" gap={1} justifyContent="center" flexWrap="wrap" sx={{ mt: 2 }}>
            {!runningHere ? (
              <Button
                variant="contained"
                startIcon={busy || loading ? <CircularProgress size={18} color="inherit" /> : <PlayArrowRoundedIcon />}
                onClick={() => handleStart(false)}
                disabled={!canRun || busy}
              >
                {task.myTrackedSeconds > 0 ? 'Wznów pomiar' : 'Rozpocznij pomiar'}
              </Button>
            ) : (
              <>
                <Button
                  variant="contained"
                  startIcon={<StopRoundedIcon />}
                  onClick={() => setNoteDialog({ note: '' })}
                  disabled={busy}
                >
                  Zakończ pomiar
                </Button>
                <Button variant="outlined" startIcon={<PauseRoundedIcon />} onClick={handlePause} disabled={busy}>
                  Wstrzymaj
                </Button>
              </>
            )}
          </Stack>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1.5, mt: 2 }}>
          <Figure label="Łącznie" value={formatDuration(task.trackedSeconds)} />
          <Figure label="Mój czas" value={formatDuration(task.myTrackedSeconds)} />
          <Figure label="Szacowany" value={task.estimatedMinutes ? formatDuration(task.estimatedMinutes * 60) : '—'} />
        </Box>

        <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted, mt: 1.75, lineHeight: 1.5 }}>
          Timer mierzy wyłącznie pracę nad tym zadaniem. Nie jest ewidencją obecności
          i nie służy do naliczania wynagrodzeń.
        </Typography>

        {task.sessions.length > 0 && (
          <Box sx={{ mt: 2.25, pt: 2, borderTop: `1px solid ${tokens.line}` }}>
            <Typography component="h3" sx={{ fontSize: '0.9375rem', fontWeight: 700 }}>
              Zapisane sesje
              <Box component="span" sx={{ ml: 0.75, color: tokens.muted, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {closedSessions.length}
              </Box>
            </Typography>
            <Stack divider={<Box sx={{ borderTop: `1px solid ${tokens.line}` }} />} sx={{ mt: 0.5 }}>
              {task.sessions.map((s) => {
                const person = withMemberName(s.user, members);
                const hasActions = canCorrect(s) || canDeleteSession(s);
                return (
                  <Stack key={s.id} direction="row" gap={1.25} alignItems="flex-start" sx={{ py: 1.25 }}>
                    <PersonAvatar firstName={person?.firstName} lastName={person?.lastName} fallback={person?.email} seed={s.userId} size={32} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="baseline" justifyContent="space-between" gap={1}>
                        <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 600, minWidth: 0 }}>
                          {userLabel(person)}
                        </Typography>
                        {s.endedAt ? (
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                            {s.durationSeconds !== null ? formatDuration(s.durationSeconds) : '—'}
                          </Typography>
                        ) : (
                          <StatusPill label="Trwa" tone="success" size="small" />
                        )}
                      </Stack>
                      <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted, fontVariantNumeric: 'tabular-nums' }}>
                        {sessionRange(s)}
                      </Typography>
                      {(s.isManual || s.note) && (
                        <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap" sx={{ mt: 0.5 }}>
                          {s.isManual && (
                            <Tooltip title={s.editReason || 'Wpis skorygowany ręcznie'}>
                              <Box component="span" sx={{ display: 'inline-flex' }}>
                                <StatusPill label="Korekta" tone="neutral" size="small" noDot />
                              </Box>
                            </Tooltip>
                          )}
                          {s.note && (
                            <Typography sx={{ fontSize: '0.8125rem', color: tokens.inkSoft, wordBreak: 'break-word' }}>
                              {s.note}
                            </Typography>
                          )}
                        </Stack>
                      )}
                    </Box>
                    {hasActions && (
                      <IconButton
                        size="small"
                        aria-label="Opcje wpisu czasu"
                        onClick={(e) => setSessionMenu({ el: e.currentTarget, session: s })}
                        sx={{ mr: -0.75, mt: -0.25 }}
                      >
                        <MoreVertIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    )}
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        )}
      </SectionCard>

      <Menu
        anchorEl={sessionMenu?.el}
        open={!!sessionMenu}
        onClose={() => setSessionMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {sessionMenu && canCorrect(sessionMenu.session) && (
          <MenuItem onClick={() => { openCorrection(sessionMenu.session); setSessionMenu(null); }}>
            Popraw wpis
          </MenuItem>
        )}
        {sessionMenu && canDeleteSession(sessionMenu.session) && (
          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() => { setDeleting({ session: sessionMenu.session, reason: '' }); setSessionMenu(null); }}
          >
            Usuń wpis
          </MenuItem>
        )}
      </Menu>

      {/* Stop with an optional note */}
      <Dialog open={!!noteDialog} onClose={() => setNoteDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Zakończ pomiar czasu</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth label="Notatka do sesji"
            value={noteDialog?.note || ''}
            onChange={(e) => setNoteDialog({ note: e.target.value })}
            placeholder="np. Przygotowanie stanowiska"
            helperText="Opcjonalnie — co udało się zrobić w tym czasie."
            inputProps={{ maxLength: 300 }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setNoteDialog(null)}>Anuluj</Button>
          <Button
            variant="contained"
            disabled={busy}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={() => handleStop(noteDialog?.note?.trim() || undefined)}
          >
            Zakończ pomiar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Timer already running elsewhere */}
      <Dialog open={!!conflict} onClose={clearConflict} maxWidth="xs" fullWidth>
        <DialogTitle>Masz już uruchomiony pomiar</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: tokens.inkSoft }}>{conflict?.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={clearConflict}>Anuluj</Button>
          {session && (
            <Button variant="outlined" onClick={() => { clearConflict(); window.location.href = `/tasks/${session.taskId}`; }}>
              Przejdź do tamtego zadania
            </Button>
          )}
          <Button
            variant="contained"
            onClick={async () => { clearConflict(); await handleStart(true); }}
          >
            Zakończ poprzedni i zacznij tutaj
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual correction */}
      <Dialog open={!!correcting} onClose={() => setCorrecting(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Popraw wpis czasu</DialogTitle>
        <DialogContent>
          <Stack gap={2}>
            {correctError && !reasonFieldError && <Alert severity="error">{correctError}</Alert>}
            <Alert severity="info">
              Każda korekta jest zapisywana w historii zadania wraz z autorem i powodem.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Początek" type="datetime-local" InputLabelProps={{ shrink: true }} fullWidth
                  value={correctForm.startedAt}
                  onChange={(e) => setCorrectForm({ ...correctForm, startedAt: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Koniec" type="datetime-local" InputLabelProps={{ shrink: true }} fullWidth
                  value={correctForm.endedAt}
                  onChange={(e) => setCorrectForm({ ...correctForm, endedAt: e.target.value })}
                />
              </Grid>
            </Grid>
            <TextField
              label="Notatka" fullWidth value={correctForm.note}
              onChange={(e) => setCorrectForm({ ...correctForm, note: e.target.value })}
              inputProps={{ maxLength: 300 }}
            />
            <TextField
              label="Powód korekty" required fullWidth multiline minRows={2}
              value={correctForm.reason}
              onChange={(e) => {
                setCorrectForm({ ...correctForm, reason: e.target.value });
                if (reasonFieldError) setCorrectError('');
              }}
              error={reasonFieldError}
              helperText={reasonFieldError ? correctError : 'Np. „Pomiar nie został zatrzymany po skończeniu pracy”. Powód trafi do historii zadania.'}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setCorrecting(null)}>Anuluj</Button>
          <Button variant="contained" onClick={submitCorrection}>Zapisz korektę</Button>
        </DialogActions>
      </Dialog>

      {/* Delete a time entry */}
      <Dialog open={!!deleting} onClose={() => setDeleting(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Usunąć wpis czasu?</DialogTitle>
        <DialogContent>
          {deleting && (
            <Box sx={{ bgcolor: tokens.ground, borderRadius: '10px', px: 1.75, py: 1.5, mb: 2 }}>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 700 }}>
                {userLabel(withMemberName(deleting.session.user, members))}
                {deleting.session.durationSeconds !== null && ` · ${formatDuration(deleting.session.durationSeconds)}`}
              </Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted, fontVariantNumeric: 'tabular-nums' }}>
                {sessionRange(deleting.session)}
              </Typography>
            </Box>
          )}
          <TextField
            autoFocus fullWidth multiline minRows={2} label="Powód usunięcia" required
            value={deleting?.reason || ''}
            onChange={(e) => setDeleting((d) => (d ? { ...d, reason: e.target.value } : d))}
            helperText="Co najmniej 3 znaki. Wpis zniknie z sumy czasu, a powód zostanie w historii zadania."
          />
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setDeleting(null)}>Anuluj</Button>
          <Button
            color="error" variant="contained"
            disabled={(deleting?.reason.trim().length || 0) < 3}
            onClick={submitDelete}
          >
            Usuń wpis
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TaskTimerPanel;
